import { useEffect } from 'react';

import { useLocation } from 'react-router';

export const useScrollToAnchor = () => {
  const location = useLocation();

  const anchorId = location.state?.scrollTo;
  const hasHighlight =
    location.state?.hasHighlight === undefined ? true : location.state?.hasHighlight;

  useEffect(() => {
    if (!anchorId) return;

    let observer: MutationObserver | null = null;
    let scrolled = false;

    const tryToScrollToAnchor = () => {
      return new Promise<boolean>((resolve) => {
        const anchor = document.getElementById(anchorId);

        if (anchor) {
          // Use requestAnimationFrame for smoother scrolling
          requestAnimationFrame(() => {
            anchor.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          });

          if (hasHighlight) {
            // Highlight with a slight delay to ensure DOM is painted
            setTimeout(() => {
              anchor.classList.add('highlighted');

              resolve(true);

              setTimeout(() => {
                anchor.classList.remove('highlighted');
              }, 2000);
            }, 100);
          } else {
            resolve(true);
          }
        } else {
          resolve(false);
        }
      });
    };

    (async () => {
      if (await tryToScrollToAnchor()) {
        scrolled = true;
        return;
      }
    })().catch(console.error);

    setTimeout(() => {
      void (async () => {
        if (!scrolled && (await tryToScrollToAnchor())) {
          scrolled = true;
          observer?.disconnect();
        }
      })();
    }, 200);

    // Otherwise, observe DOM changes
    observer = new MutationObserver((mutations) => {
      mutations.forEach(async (mutation) => {
        if (mutation.type === 'childList') {
          if (!scrolled && (await tryToScrollToAnchor())) {
            scrolled = true;
            observer?.disconnect();
          }
        }
      });
    });

    // Observe only changes in the body to improve performance
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [anchorId]);
};
